<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: String, default: 'custom' },
  direction: { type: String, default: 'asc' },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'update:direction', 'open']);

const { t } = useI18n();
const open = ref(false);
const search = ref('');
const highlightIndex = ref(0);
const triggerEl = ref(null);
const dropdownPos = ref({ top: 0, right: 0 });

function calcPos(el) {
  if (!el) return { top: 0, right: 0 };
  const rect = el.getBoundingClientRect();
  return { top: rect.bottom + 4, right: window.innerWidth - rect.right };
}

const SORT_OPTIONS = [
  { key: 'custom', label: t('TASKS.SORT.CUSTOM') },
  { key: 'priority', label: t('TASKS.SORT.PRIORITY') },
  { key: 'due_date', label: t('TASKS.SORT.DUE_DATE') },
  { key: 'created_at', label: t('TASKS.SORT.CREATED') },
  { key: 'title', label: t('TASKS.SORT.TITLE') },
];

const ALL_LABEL = 'todos';

const filteredOptions = computed(() => {
  const q = search.value.toLowerCase();
  if (!q) return SORT_OPTIONS;
  return SORT_OPTIONS.filter(o => o.label.toLowerCase().includes(q)).sort(
    (a, b) =>
      a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
  );
});

const navigable = computed(() => {
  const q = search.value.toLowerCase();
  if (!q) return SORT_OPTIONS.map(o => ({ id: o.key }));
  const items = filteredOptions.value.map(o => ({ id: o.key }));
  return ALL_LABEL.includes(q) ? [{ id: '' }, ...items] : items;
});

watch(navigable, () => {
  highlightIndex.value = 0;
});
watch(search, () => {
  highlightIndex.value = 0;
});

const currentLabel = computed(
  () =>
    SORT_OPTIONS.find(o => o.key === props.modelValue)?.label ??
    t('TASKS.SORT.CUSTOM')
);

function selectSort(key) {
  emit('update:modelValue', key);
  if (key === 'custom') emit('update:direction', 'asc');
  open.value = false;
  search.value = '';
}

function toggleDirection() {
  emit('update:direction', props.direction === 'asc' ? 'desc' : 'asc');
}

function onWindowKeydown(e) {
  if (!open.value) return;
  if (e.key === 'Escape') {
    open.value = false;
    search.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    highlightIndex.value = Math.min(
      highlightIndex.value + 1,
      navigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    highlightIndex.value = Math.max(highlightIndex.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = navigable.value[highlightIndex.value];
    if (item) selectSort(item.id);
  } else if (e.key === 'Backspace') {
    search.value = search.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    search.value += e.key;
  }
}

watch(open, val => {
  if (val) {
    search.value = '';
    highlightIndex.value = 0;
    window.addEventListener('keydown', onWindowKeydown);
  } else {
    window.removeEventListener('keydown', onWindowKeydown);
    search.value = '';
  }
});

function onClickOutside(e) {
  if (
    !e.target.closest('[data-task-sort]') &&
    !e.target.closest('[data-task-sort-dropdown]')
  )
    open.value = false;
}

onMounted(() => document.addEventListener('mousedown', onClickOutside));
onUnmounted(() => {
  window.removeEventListener('keydown', onWindowKeydown);
  document.removeEventListener('mousedown', onClickOutside);
});

function toggleOpen() {
  open.value = !open.value;
  if (open.value) {
    dropdownPos.value = calcPos(triggerEl.value);
    emit('open');
  }
}

defineExpose({
  close: () => {
    open.value = false;
  },
});
</script>

<template>
  <div data-task-sort class="flex items-center gap-1 flex-shrink-0">
    <div class="relative">
      <button
        ref="triggerEl"
        class="flex items-center gap-1 border rounded-lg transition-colors focus:outline-none"
        :class="[
          compact ? 'text-xs px-2 py-1' : 'text-sm px-2.5 py-1.5',
          modelValue !== 'custom'
            ? 'border-n-brand text-n-brand bg-n-alpha-2'
            : 'border-n-weak text-n-slate-11 bg-n-background hover:border-n-container',
        ]"
        @click="toggleOpen"
      >
        <span
          :class="
            compact
              ? 'i-lucide-arrow-up-down size-3'
              : 'i-lucide-arrow-up-down size-3.5'
          "
          class="flex-shrink-0"
        />
        <span v-if="open && search" class="text-n-slate-10">{{ search }}</span>
        <span v-else>{{ currentLabel }}</span>
        <span
          :class="
            compact
              ? 'i-lucide-chevron-down size-3'
              : 'i-lucide-chevron-down size-3.5'
          "
          class="text-n-slate-10"
        />
      </button>

      <Teleport to="body">
        <div
          v-if="open"
          data-task-sort-dropdown
          class="fixed z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-36 overflow-hidden py-1"
          :style="{
            top: dropdownPos.top + 'px',
            right: dropdownPos.right + 'px',
          }"
          @click.stop
        >
          <button
            v-for="(opt, idx) in filteredOptions"
            :key="opt.key"
            class="w-full flex items-center gap-2 hover:bg-n-alpha-1 transition-colors"
            :class="[
              compact ? 'text-xs px-2 py-1.5' : 'text-sm px-3 py-2',
              idx === highlightIndex
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : modelValue === opt.key
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12',
            ]"
            @click="selectSort(opt.key)"
          >
            <span
              v-if="modelValue === opt.key"
              class="i-lucide-check size-3 flex-shrink-0 text-n-brand"
            />
            <span v-else class="size-3 flex-shrink-0" />
            {{ opt.label }}
          </button>
          <p
            v-if="search && !navigable.length"
            class="text-n-slate-10 italic"
            :class="compact ? 'text-xs px-2 py-1.5' : 'text-sm px-3 py-2'"
          >
            {{ t('TASKS.FILTERS.NO_RESULTS') }}
          </p>
        </div>
      </Teleport>
    </div>

    <!-- Botão ASC/DESC (oculto no modo personalizado) -->
    <button
      v-if="modelValue !== 'custom'"
      class="flex items-center justify-center size-[30px] border rounded-lg transition-colors border-n-weak text-n-slate-11 bg-n-background hover:border-n-container hover:bg-n-alpha-1"
      :title="direction === 'asc' ? t('TASKS.SORT.ASC') : t('TASKS.SORT.DESC')"
      @click="toggleDirection"
    >
      <span
        class="size-3.5 transition-transform"
        :class="
          direction === 'asc'
            ? 'i-lucide-arrow-up-narrow-wide'
            : 'i-lucide-arrow-down-wide-narrow'
        "
      />
    </button>
  </div>
</template>
